import {FC, Fragment, useState} from 'react'
import {HttpGetSignatureSheetsResponse} from '../../http/accesses.http'
import {StyledAccessesValidatorListItemStatusSquare, StyledAccordionItemContainer} from './style'
import {Text} from '@/components/ui/text/Text'
import {AlertCircleIcon, CheckIcon, Pencil01Icon, Trash01Icon, XCloseIcon} from '@/components/ui/icon/Icon'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {formatLocaleDate} from '@/dayjs'
import {useTranslation} from '@/translations/i18n'
import {accessGroupNameEnumToLabel, eventTypeMapToLabel} from '@/features/agenda/utils'
import {Button} from '@/components/ui/button/Button'
import {projectSectorToLabel} from '@/features/projects/utils'
import {EditAbsenceDialog} from '../edit-absence-dialog/EditAbsenceDialog'
import {EditAccessDialog} from '../edit-access-dialog/EditAccessDialog'
import {DeleteAccessModal} from '../accesses-other-action-menu/DeleteAccessModal'
import {accessTypeToLabel} from '../../utils'
import {useTheme} from 'styled-components'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {useValidateAccesses} from '../../queries/useValidateAccesses'
import {QUERY_KEYS} from '@/queryClient'
import {toast} from '@/utilities/toast/toast'
import {useUpdateAccess} from '../../queries/useUpdateAccess'
import {useQueryClient} from '@tanstack/react-query'
import {Spinner} from '@/components/ui/spinner/Spinner'

type AccessesValidatorListItemAccessProps = {
    access: HttpGetSignatureSheetsResponse[0]['accesses'][0]
}

export const AccessesValidatorListItemAccess: FC<AccessesValidatorListItemAccessProps> = ({access}) => {
    const {t} = useTranslation()
    const [isDeleteAccessModalOpen, setIsDeleteAccessModalOpen] = useState(false)
    const {palette} = useTheme()
    const queryClient = useQueryClient()
    const {mutate: validateAccess, isPending: isValidating} = useValidateAccesses({
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACCESSES]})
            toast.success(t('accesses:access_validated_success', {count: 1}))
        }
    })
    const {mutate: updateAccess, isPending: isUpdating} = useUpdateAccess(
        {accessId: access.id},
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACCESSES]})
                toast.success(t('accesses:access_invalidated_success'))
            }
        }
    )

    return (
        <Fragment>
            <StyledAccordionItemContainer>
                <Text size="textSm" palette="neutral" shade="600">
                    {formatLocaleDate(access.event.date, 'DD/MM/YYYY')}
                </Text>
                <Flexbox gap={1} direction="column">
                    {access.type === 'absence' && (
                        <Flexbox gap={1}>
                            <Text size="textSm" palette="neutral" shade="600">
                                {t(accessTypeToLabel[access.type])}
                            </Text>
                            {access.event.communicatedOnTime === null && (
                                <Tooltip variant="light" content={t('accesses:absence_not_communicated')}>
                                    <AlertCircleIcon color={palette.orange['500']} size={15} />
                                </Tooltip>
                            )}
                        </Flexbox>
                    )}
                    <Text size="textSm" palette="neutral" weight="500" shade="600">
                        {access.event.eventConsultants?.[0]?.accessType?.groupName
                            ? t(accessGroupNameEnumToLabel[access.event.eventConsultants?.[0]?.accessType?.groupName])
                            : '-'}
                    </Text>
                </Flexbox>
                <Text size="textSm" palette="neutral" shade="600">
                    {access.event.eventConsultants?.[0]?.accessType?.accessType
                        ? t(eventTypeMapToLabel[access.event.eventConsultants?.[0]?.accessType?.accessType])
                        : '-'}
                </Text>
                <Text size="textSm" palette="neutral" shade="600">
                    {access.remappedSiarAccessType ?? '-'}
                </Text>
                <Text size="textSm" palette="neutral" shade="600">
                    {access.sector ? t(projectSectorToLabel[access.sector]) : '-'}
                </Text>
                <StyledAccessesValidatorListItemStatusSquare $status={access.status} />
                <Flexbox gap={1} fullWidth justify="end">
                    {!(access.type === 'absence' && access.event.communicatedOnTime == null) && (
                        <>
                            {access.status == 'to_validate' ? (
                                <Tooltip
                                    variant="light"
                                    content={t('accesses:validate_access', {count: 1})}
                                    isOpenOnClickEnabled={false}
                                    triggerProps={{asChild: true}}
                                >
                                    <Button
                                        onClick={() => validateAccess({accessIds: [access.id]})}
                                        variant="ghost"
                                        size="xs"
                                        shape="square"
                                        disabled={isValidating}
                                    >
                                        {isValidating ? <Spinner /> : <CheckIcon />}
                                    </Button>
                                </Tooltip>
                            ) : (
                                access.status == 'validated' && (
                                    <Tooltip
                                        variant="light"
                                        content={t('accesses:invalidate_access')}
                                        isOpenOnClickEnabled={false}
                                        triggerProps={{asChild: true}}
                                    >
                                        <Button
                                            onClick={() => updateAccess({status: 'to_validate'})}
                                            variant="ghost"
                                            size="xs"
                                            shape="square"
                                            disabled={isUpdating}
                                        >
                                            {isUpdating ? <Spinner /> : <XCloseIcon />}
                                        </Button>
                                    </Tooltip>
                                )
                            )}
                        </>
                    )}
                    {access.type === 'access' && (
                        <EditAccessDialog
                            access={access}
                            trigger={
                                <div>
                                    <Tooltip
                                        variant="light"
                                        content={t('commons:edit')}
                                        isOpenOnClickEnabled={false}
                                        triggerProps={{asChild: true}}
                                    >
                                        <Button
                                            disabled={access.status === 'validated'}
                                            variant="ghost"
                                            size="xs"
                                            shape="square"
                                        >
                                            <Pencil01Icon />
                                        </Button>
                                    </Tooltip>
                                </div>
                            }
                        />
                    )}
                    {access.type === 'absence' && (
                        <EditAbsenceDialog
                            access={access}
                            trigger={
                                <div>
                                    <Tooltip
                                        variant="light"
                                        content={t('commons:edit')}
                                        isOpenOnClickEnabled={false}
                                        triggerProps={{asChild: true}}
                                    >
                                        <Button
                                            disabled={access.status === 'validated'}
                                            variant="ghost"
                                            size="xs"
                                            shape="square"
                                        >
                                            <Pencil01Icon />
                                        </Button>
                                    </Tooltip>
                                </div>
                            }
                        />
                    )}
                    <Tooltip variant="light" content={t('commons:delete')}>
                        <Button
                            variant="ghost"
                            size="xs"
                            shape="square"
                            onClick={() => setIsDeleteAccessModalOpen(true)}
                        >
                            <Trash01Icon />
                        </Button>
                    </Tooltip>
                </Flexbox>
            </StyledAccordionItemContainer>
            {isDeleteAccessModalOpen && (
                <DeleteAccessModal access={access} onClose={() => setIsDeleteAccessModalOpen(false)} />
            )}
        </Fragment>
    )
}
